import { Link } from "react-router-dom";
import useSWR from "swr";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import ImageFrame from "@/components/site/ImageFrame";
import Reveal from "@/components/site/Reveal";
import SectionHeading from "@/components/site/SectionHeading";
import Seo from "@/components/site/Seo";
import AvvisiBanner from "@/components/site/AvvisiBanner";
import ReservationWidget from "@/components/ReservationWidget";

const fetcher = (url) => api.get(url).then((r) => r.data);

function Hero({ settings }) {
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, reduce ? 0 : 90]);
  const title = settings.hero_title || "Nel cuore del borgo,\nla cucina del territorio";
  return (
    <section className="relative min-h-[100svh] flex items-end overflow-hidden bg-wood-deep" data-testid="hero">
      <motion.div style={{ y }} className="absolute inset-0">
        <ImageFrame
          src={settings.hero_image}
          label="HERO PHOTO"
          alt="L'Osteria Aterrana nel borgo storico di Aterrana"
          ratio="auto"
          priority
          dark
          className="h-full w-full [aspect-ratio:auto]"
        />
        <span className="absolute inset-0 bg-gradient-to-t from-wood-deep via-wood-deep/45 to-wood-deep/15" />
      </motion.div>

      <div className="container-ed relative z-10 pb-28 pt-40 md:pb-32">
        <motion.p
          className="eyebrow text-gold"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
        >
          {settings.hero_eyebrow || "Aterrana · Montoro · Irpinia"}
        </motion.p>
        <motion.h1
          className="mt-7 max-w-[22ch] font-serif text-[2.7rem] leading-[1.03] tracking-tight text-cream sm:text-6xl lg:text-[5.2rem]"
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.22, 0.61, 0.36, 1] }}
        >
          {title.split("\n").map((line) => (
            <span key={line} className="block">
              {line}
            </span>
          ))}
        </motion.h1>
        <motion.p
          className="mt-7 max-w-md text-sm md:text-lg text-cream/70 leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.55 }}
        >
          {settings.hero_subtitle || "Una piccola osteria ad Aterrana, tra Montoro e l'Irpinia."}
        </motion.p>
        <motion.div
          className="mt-11 flex flex-col sm:flex-row gap-4 sm:items-center"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          <Link
            to="/prenota"
            data-testid="hero-book"
            className="inline-flex items-center justify-center px-9 py-4 bg-cream text-wood text-[0.7rem] uppercase tracking-[0.22em] hover:bg-gold transition-colors duration-500"
          >
            Prenota un tavolo
          </Link>
          <Link
            to="/la-cucina"
            data-testid="hero-cuisine"
            className="inline-flex items-center justify-center gap-2 px-9 py-4 border border-cream/40 text-cream text-[0.7rem] uppercase tracking-[0.22em] hover:bg-cream hover:text-wood transition-colors duration-500"
          >
            Scopri la cucina <ArrowRight size={14} strokeWidth={1.4} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function Intro({ settings }) {
  return (
    <section className="section-y bg-cream" data-testid="intro-section">
      <div className="container-ed grid gap-14 lg:grid-cols-12 lg:gap-16 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-32">
          <div className="flex items-center gap-4">
            <span className="rule" aria-hidden="true" />
            <p className="eyebrow text-sage">L'osteria</p>
          </div>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.05] text-wood">
            {settings.story_title || "Una porta aperta nel borgo"}
          </h2>
        </div>
        <Reveal className="lg:col-span-6 lg:col-start-7 prose-ed text-sm md:text-lg text-foreground/80">
          {(settings.story_text || "").split("\n\n").map((p) => (
            <p key={p.slice(0, 60)}>{p}</p>
          ))}
          <Link
            to="/il-borgo"
            className="mt-2 inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.2em] text-sage link-underline"
          >
            Il borgo di Aterrana <ArrowRight size={14} strokeWidth={1.4} />
          </Link>
          <div className="mt-10">
            <ImageFrame
              src={settings.story_image}
              label="INTERIOR PHOTO"
              alt="La sala dell'osteria: tavoli apparecchiati, travi in legno e muri in pietra"
              ratio="16 / 10"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Cucina({ featured, settings }) {
  return (
    <section className="section-y bg-cream" data-testid="cuisine-section">
      <div className="container-ed grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow="La cucina"
            title="Ricette di casa, cotture lunghe"
            lead="Cucina tradizionale irpina e campana: la genovese con la cipolla di Montoro, il ragù cotto piano, il baccalà, le alici, il mallone. Scegliamo con attenzione ciò che entra in cucina e lavoriamo con quello che la stagione mette a disposizione."
          />
          <Reveal delay={0.15} className="mt-10">
            <ImageFrame
              src={settings.cuisine_image}
              label="DISH PHOTO"
              alt="Un primo piatto della cucina dell'osteria"
              ratio="4 / 3"
            />
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <ol className="divide-y divide-wood/12 border-t border-wood/12" data-testid="featured-dishes">
            {(featured || []).slice(0, 6).map((item, i) => (
              <Reveal as="li" key={item.id} delay={i * 0.06} className="py-7 flex gap-6">
                <span className="font-serif text-sm text-gold pt-1.5 tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="flex-1">
                  <div className="flex items-baseline justify-between gap-6">
                    <h3 className="font-serif text-2xl md:text-[1.7rem] text-wood leading-tight">
                      {item.name}
                    </h3>
                  </div>
                  {item.description && (
                    <p className="mt-2 text-sm md:text-base text-foreground/65 leading-relaxed">
                      {item.description}
                    </p>
                  )}
                </div>
              </Reveal>
            ))}
          </ol>
          <Reveal className="mt-10">
            <Link
              to="/menu"
              data-testid="home-menu-link"
              className="inline-flex items-center gap-2 px-8 py-4 border border-wood/30 text-wood text-[0.7rem] uppercase tracking-[0.22em] hover:bg-wood hover:text-cream transition-colors duration-500"
            >
              Scopri il menu <ArrowRight size={14} strokeWidth={1.4} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Territorio({ settings }) {
  return (
    <section className="section-y bg-cream" data-testid="territory-section">
      <div className="container-ed">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Il territorio" title={settings.borgo_title || "Aterrana, Montoro e l'Irpinia"} />
            <Reveal delay={0.1} className="mt-8 prose-ed text-sm md:text-lg text-foreground/78">
              {(settings.borgo_text || "").split("\n\n").map((p) => (
                <p key={p.slice(0, 60)}>{p}</p>
              ))}
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/il-borgo"
                data-testid="home-borgo-link"
                className="mt-9 inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.2em] text-sage link-underline"
              >
                Scopri il borgo <ArrowRight size={14} strokeWidth={1.4} />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-5">
            <Reveal className="mt-10">
              <ImageFrame
                src={settings.territory_image_1}
                label="BORGO PHOTO"
                alt="La piazza del borgo con la chiesa e il portico"
                ratio="3 / 4"
              />
            </Reveal>
            <Reveal delay={0.12}>
              <ImageFrame
                src={settings.territory_image_2}
                label="BORGO PHOTO"
                alt="Gerani su un davanzale in pietra ad Aterrana"
                ratio="3 / 4"
              />
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function BookingBlock() {
  return (
    <section className="relative bg-sage-deep grain py-24 md:py-32" data-testid="booking-section">
      <div className="container-ed grid gap-14 lg:grid-cols-12 items-start">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-4">
            <span className="rule" aria-hidden="true" />
            <p className="eyebrow text-gold">Prenota</p>
          </div>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl leading-[1.06] text-cream">
            Ti aspettiamo ad Aterrana
          </h2>
          <p className="mt-6 text-sm md:text-base text-cream/70 leading-relaxed max-w-sm">
            La sala è piccola: meglio prenotare. Scegli data, orario e siamo a posto.
          </p>
        </div>
        <div className="lg:col-span-7 lg:col-start-6 min-w-0 bg-cream p-6 md:p-10">
          <ReservationWidget />
        </div>
      </div>
    </section>
  );
}

function ProgettiTeaser({ settings }) {
  return (
    <section className="bg-beige/60 py-20 md:py-24" data-testid="progetti-teaser">
      <div className="container-ed flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-2xl">
          <div className="flex items-center gap-4">
            <span className="rule" aria-hidden="true" />
            <p className="eyebrow text-sage">Progetti futuri</p>
          </div>
          <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-[2.8rem] leading-[1.1] text-wood">
            L'osteria è il primo passo. Il progetto guarda al borgo.
          </h2>
          <p className="mt-5 text-sm md:text-base text-foreground/70 leading-relaxed">
            Nel tempo vorremmo coltivare parte di ciò che portiamo in tavola e riportare ospitalità
            nelle case di Aterrana. Sono progetti ancora da costruire, un passo alla volta.
          </p>
        </div>
        <Link
          to="/progetti-futuri"
          data-testid="home-progetti-link"
          className="inline-flex items-center gap-2 shrink-0 px-8 py-4 border border-wood/30 text-wood text-[0.7rem] uppercase tracking-[0.2em] hover:bg-wood hover:text-cream transition-colors duration-500"
        >
          Scopri i progetti futuri <ArrowRight size={14} strokeWidth={1.4} />
        </Link>
      </div>
    </section>
  );
}

function IdeeTeaser() {
  return (
    <section className="bg-cream py-20 md:py-24" data-testid="idee-teaser">
      <div className="container-ed flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-2xl">
          <div className="flex items-center gap-4">
            <span className="rule" aria-hidden="true" />
            <p className="eyebrow text-gold">Le idee di chi legge</p>
          </div>
          <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-[2.8rem] leading-[1.1] text-wood">
            Hai un'idea per il borgo?
          </h2>
          <p className="mt-5 text-sm md:text-base text-foreground/70 leading-relaxed">
            Un sentiero, uno spazio da riaprire, un servizio che manca. Raccontacela: le leggiamo
            una per una e le più belle finiscono nel Diario, con la firma che scegli tu.
          </p>
        </div>
        <Link
          to="/diario-dal-borgo/idee-per-il-borgo#modulo-idee"
          data-testid="home-idee-link"
          className="inline-flex items-center gap-2 shrink-0 px-8 py-4 border border-wood/30 text-wood text-[0.7rem] uppercase tracking-[0.2em] hover:bg-wood hover:text-cream transition-colors duration-500"
        >
          Condividi la tua idea <ArrowRight size={14} strokeWidth={1.4} />
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  const { settings } = useSettings();
  const { data: featured } = useSWR("/menu/featured", fetcher, { revalidateOnFocus: false });
  return (
    <>
      <Seo
        title={settings.seo_home_title || "Osteria Aterrana · Ristorante nel borgo di Aterrana, Montoro (AV)"}
        description={settings.seo_home_description}
        path="/"
      />
      <Hero settings={settings} />
      <AvvisiBanner />
      <Intro settings={settings} />
      <Cucina featured={featured} settings={settings} />
      <Territorio settings={settings} />
      <ProgettiTeaser settings={settings} />
      <IdeeTeaser />
      <BookingBlock />
    </>
  );
}
